/**
 * The preview of a page the HTML agent is still writing. Drafts arrive about once a second;
 * swapping the frame's `srcdoc` for each would reload it (a flash, the scroll back at the top,
 * images refetched). Instead the frame loads once, with a bridge script, and each later draft
 * is posted to it and patched into the page in place: only nodes that changed are touched, so
 * the scroll, images and fonts stay.
 *
 * The sandboxed frame has no same-origin access, so everything goes over `postMessage`: the
 * frame says when it is ready for drafts, and reports its scroll so the finished page, which
 * does load fresh, opens where the reader was.
 *
 * Drafts are posted as raw source and parsed in the frame only, so the chat's main thread
 * does no page parsing while the page is written.
 */
export const DRAFT_HTML_MESSAGE_TYPE = 'evergpt:preview-draft-html'
export const DRAFT_READY_MESSAGE_TYPE = 'evergpt:preview-draft-ready'
export const DRAFT_SCROLL_MESSAGE_TYPE = 'evergpt:preview-draft-scroll'

/** Marks the nodes the preview adds to a page; draft patches leave them alone. */
export const PREVIEW_NODE_ATTRIBUTE = 'data-evergpt-preview'

/** The stylesheet the backend compiles into every draft: it is whole from the first one. */
const GENERATED_STYLE =
  /<style\b[^>]*\bid=["']?uno-generated\b[^>]*>[\s\S]*?<\/style\s*>/gi
const STYLE_END = /<\/style\s*>/i
const BODY_OPENING = /<body\b[^>]*>/i
/** What a page fragment may lead with before its content: the backend moves these to the head. */
const LEADING_HEAD_NODES =
  /^(?:\s+|<!--[\s\S]*?-->|<(?:meta|link)\b[^>]*>|<title\b[^>]*>[\s\S]*?<\/title\s*>)*/i
/** Text, or a whole opening tag that does not belong in the head. */
const PAGE_CONTENT_START =
  /^(?:[^<]|<(?!(?:style|title|meta|link)\b)[a-z][\w-]*\b[^>]*>)/i

/**
 * The draft has the page's own styles: its `<style>` is closed, or its body starts with
 * content and no `<style>`. The agent writes a fragment that drafts wrap in `<body>`, so
 * the body is there before the fragment's leading `<style>` is. The backend's generated
 * stylesheet does not count.
 */
export function isDraftPageStyled(html: string): boolean {
  const authored = html.replace(GENERATED_STYLE, '')
  if (STYLE_END.test(authored)) return true
  const bodyOpening = BODY_OPENING.exec(authored)
  if (!bodyOpening) return false
  const body = authored
    .slice(bodyOpening.index + bodyOpening[0].length)
    .replace(LEADING_HEAD_NODES, '')
  return PAGE_CONTENT_START.test(body)
}

/** Restores `scrollY` once laid out, then reports each scroll to the parent. */
export function previewDraftScrollScriptSource(scrollY: number): string {
  const y = Number.isFinite(scrollY) ? Math.max(0, Math.round(scrollY)) : 0
  return `(function(){var y=${y};function restore(){if(y)scrollTo(0,y)}
addEventListener('DOMContentLoaded',restore);addEventListener('load',restore);
addEventListener('scroll',function(){parent.postMessage({type:'${DRAFT_SCROLL_MESSAGE_TYPE}',y:scrollY},'*')},{passive:true})})()`
}

/**
 * Takes later drafts in place. Nodes are matched by position: a draft mostly grows at its end,
 * so the page written so far is left alone. Scripts are never added or run here (the finished
 * page loads with its own).
 */
export function previewDraftBridgeScriptSource(): string {
  return `(function(){
var MARK='${PREVIEW_NODE_ATTRIBUTE}';
function syncAttributes(from,to){
  for(var i=from.attributes.length-1;i>=0;i--){var name=from.attributes[i].name;if(!to.hasAttribute(name))from.removeAttribute(name)}
  for(var j=0;j<to.attributes.length;j++){var a=to.attributes[j];if(from.getAttribute(a.name)!==a.value)from.setAttribute(a.name,a.value)}
}
function pageNodes(el){var out=[];for(var n=el.firstChild;n;n=n.nextSibling){if(!(n.nodeType===1&&n.hasAttribute(MARK)))out.push(n)}return out}
function morph(from,to){
  if(from.nodeType!==1){if(from.nodeValue!==to.nodeValue)from.nodeValue=to.nodeValue;return}
  if(from!==document.documentElement&&from!==document.head&&from!==document.body)syncAttributes(from,to);
  if(from.nodeName==='STYLE'||from.nodeName==='TEXTAREA'){if(from.textContent!==to.textContent)from.textContent=to.textContent;return}
  var kids=pageNodes(from),next=pageNodes(to);
  for(var i=0;i<next.length;i++){
    var current=kids[i];
    if(!current)from.appendChild(document.importNode(next[i],true));
    else if(current.nodeType===next[i].nodeType&&current.nodeName===next[i].nodeName)morph(current,next[i]);
    else from.replaceChild(document.importNode(next[i],true),current);
  }
  for(var k=next.length;k<kids.length;k++)from.removeChild(kids[k]);
}
addEventListener('message',function(event){
  var data=event.data;
  if(event.source!==parent||!data||data.type!=='${DRAFT_HTML_MESSAGE_TYPE}')return;
  var doc=new DOMParser().parseFromString(data.html,'text/html');
  doc.querySelectorAll('script,meta[http-equiv="Content-Security-Policy" i]').forEach(function(el){el.remove()});
  syncAttributes(document.documentElement,doc.documentElement);
  syncAttributes(document.body,doc.body);
  morph(document.head,doc.head);
  morph(document.body,doc.body);
});
addEventListener('DOMContentLoaded',function(){parent.postMessage({type:'${DRAFT_READY_MESSAGE_TYPE}'},'*')});
})()`
}
